import type { UploadedFile } from '@engagement-web-app/common';
import { formatDate } from 'date-fns';
import { Download, File, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../Button';
import { Dialog, DialogButtonGroup, DialogClose } from '../Dialog';
import { Icon } from '../Icon';
import { Table } from '../Table';
import { TextButton } from '../TextButton';
import { EmbeddedDangerToast } from '../Toasts';
import * as styles from './index.css';

type FileListTableProps = {
    files: UploadedFile[];
    onDownload?: (file: UploadedFile) => void;
    onDelete?: (file: UploadedFile) => Promise<void>;
    isDeleting?: boolean;
    showDeleteButton?: boolean;
};

function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';

    const units = ['B', 'KB', 'MB', 'GB'];
    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size = bytes / k ** i;

    if (i === 0) {
        return `${bytes} B`;
    }

    return `${size.toFixed(1)} ${units[i]}`;
}

function getFileTypeLabel(contentType: string): string {
    const typeMap: Record<string, string> = {
        'application/pdf': 'PDF',
        'application/msword': 'DOC',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
        'application/vnd.ms-excel': 'XLS',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
        'application/vnd.ms-powerpoint': 'PPT',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
        'image/jpeg': 'JPEG',
        'image/png': 'PNG',
        'image/gif': 'GIF',
        'image/webp': 'WEBP',
        'image/svg+xml': 'SVG',
        'text/plain': 'TXT',
        'text/csv': 'CSV',
        'application/json': 'JSON',
        'application/zip': 'ZIP',
        'application/x-rar-compressed': 'RAR',
        'video/mp4': 'MP4',
        'audio/mpeg': 'MP3',
    };

    return typeMap[contentType] || contentType.split('/').pop()?.toUpperCase() || 'FILE';
}

export function FileListTable({
    files,
    onDownload,
    onDelete,
    isDeleting = false,
    showDeleteButton = true,
}: FileListTableProps) {
    const [deleteTarget, setDeleteTarget] = useState<UploadedFile | null>(null);

    if (files.length === 0) {
        return <div className={styles.emptyState}>ファイルがありません</div>;
    }

    const handleDelete = async (file: UploadedFile) => {
        if (!onDelete) return;
        try {
            await onDelete(file);
            setDeleteTarget(null);
        } catch {
            // Do nothing (handled by toast notification)
        }
    };

    return (
        <Table.Container>
            <Table.Root>
                <Table.Header>
                    <Table.HeaderCell>ファイル名</Table.HeaderCell>
                    <Table.HeaderCell>アップロード日時</Table.HeaderCell>
                    <Table.HeaderCell>アップロード者</Table.HeaderCell>
                    <Table.HeaderCell>サイズ</Table.HeaderCell>
                    <Table.HeaderCell>種類</Table.HeaderCell>
                    <Table.HeaderCell>アクション</Table.HeaderCell>
                </Table.Header>

                <Table.Body>
                    {files.map((file) => (
                        <Table.Row key={file.fileId}>
                            <Table.Cell>
                                <div className={styles.fileNameCell}>
                                    <Icon icon={File} size="sm" className={styles.fileIcon} />
                                    <span className={styles.fileName}>{file.fileName}</span>
                                </div>
                            </Table.Cell>
                            <Table.Cell>
                                <span className={styles.uploadedAt}>{formatDate(file.uploadedAt, 'yyyy-MM-dd')}</span>
                            </Table.Cell>
                            <Table.Cell>
                                <span className={styles.uploadedBy}>{file.uploadedBy?.name ?? '-'}</span>
                            </Table.Cell>
                            <Table.Cell>
                                <span className={styles.fileSize}>{formatFileSize(file.fileSizeBytes)}</span>
                            </Table.Cell>
                            <Table.Cell>
                                <span className={styles.fileType}>{getFileTypeLabel(file.contentType)}</span>
                            </Table.Cell>
                            <Table.Cell>
                                <div className={styles.actions}>
                                    <TextButton onClick={() => onDownload?.(file)}>
                                        <Icon icon={Download} size="sm" />
                                        ダウンロード
                                    </TextButton>
                                    {showDeleteButton && (
                                        <>
                                            <TextButton variant="danger" onClick={() => setDeleteTarget(file)}>
                                                <Icon icon={Trash2} size="sm" />
                                                削除
                                            </TextButton>

                                            <Dialog
                                                open={deleteTarget !== null}
                                                onOpenChange={(open) => !open && setDeleteTarget(null)}
                                                title="ファイルを削除する"
                                            >
                                                <EmbeddedDangerToast
                                                    title="削除のご注意お願いします"
                                                    message="削除するとファイルを取り戻せません。"
                                                />
                                                <p>
                                                    <strong className={styles.deleteTargetFileName}>
                                                        {deleteTarget?.fileName}
                                                    </strong>{' '}
                                                    を削除しますか？
                                                </p>

                                                <DialogButtonGroup>
                                                    <DialogClose>
                                                        <Button variant="secondary" disabled={isDeleting}>
                                                            キャンセル
                                                        </Button>
                                                    </DialogClose>
                                                    <Button
                                                        variant="primary"
                                                        onClick={() => deleteTarget && handleDelete(deleteTarget)}
                                                        disabled={isDeleting}
                                                    >
                                                        {isDeleting ? (
                                                            '削除中...'
                                                        ) : (
                                                            <>
                                                                <Icon icon={Trash2} size="sm" />
                                                                <span>削除する</span>
                                                            </>
                                                        )}
                                                    </Button>
                                                </DialogButtonGroup>
                                            </Dialog>
                                        </>
                                    )}
                                </div>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table.Root>
        </Table.Container>
    );
}

type FileListTableSkeletonProps = {
    rows?: number;
};

export function FileListTableSkeleton({ rows = 5 }: FileListTableSkeletonProps) {
    return (
        <Table.Container>
            <Table.Skeleton columns={6} rows={rows} />
        </Table.Container>
    );
}
